import React, { useState, useEffect } from 'react';
import '../components/Dashboard/Widgets/DashboardWidgets.css';
import Sidebar from '../components/Dashboard/Sidebar';
import Header from '../components/Dashboard/Header';
import IconMenu from '../components/Dashboard/IconMenu';
import TeamsSection from '../components/Dashboard/TeamsSection';
import NotesSection from '../components/Notes/NotesSection.jsx';
import FileManagerSection from '../components/FileManager/FileManagerSection.jsx';
import NewsFeedSection from '../components/Dashboard/NewsFeedSection.jsx';
import MinisiteSection from '../components/Minisite/MinisiteSection.jsx';
import TalkSection from '../components/Talk/TalkSection.jsx';

import CalendarSection from '../components/Calendar/CalendarSection.jsx';
import { EmailSection } from '../components/Email';
import { EmailProvider } from '../context/EmailContext';
import { ProjectsSection } from '../components/Projects';
import NoticeboardSection from '../components/Noticeboard/NoticeboardSection';

import MeetingWindow from '../components/VideoCall/MeetingWindow.jsx';
import MeetingsSection from '../components/Meetings/MeetingsSection.jsx';
import NotificationSidebar from '../components/Dashboard/NotificationSidebar';
import AddressBookPanel from '../components/AddressBook/AddressBookPanel';
import MediaPanel from '../components/SocialMedia/MediaPanel';
import { useDashboard } from '../context/DashboardContext';
import { useVideoCall } from '../context/VideoCallContext';
import { FaChevronUp } from 'react-icons/fa';
import useTitle from '../hooks/useTitle';
import { useI18n } from '../i18n';

import NewsFeedWidget from '../components/Dashboard/Widgets/NewsFeedWidget';
import EmailWidget from '../components/Dashboard/Widgets/EmailWidget';
import CalendarWidget from '../components/Dashboard/Widgets/CalendarWidget';
import ProjectsWidget from '../components/Dashboard/Widgets/ProjectsWidget';
import FilesWidget from '../components/Dashboard/Widgets/FilesWidget';

const DashboardWidgets = () => (
  <div className="dashboard-grid">
    <div className="grid-row top-widgets">
      <NewsFeedWidget />
      <EmailProvider>
        <EmailWidget />
      </EmailProvider>
    </div>
    <div className="grid-row bottom-widgets">
      <CalendarWidget />
      <ProjectsWidget />
      <FilesWidget />
    </div>
  </div>
);

const Dashboard = () => {
  const { t } = useI18n();
  useTitle('navigation.dashboard');
  const { activeView, setActiveView } = useDashboard();
  const { activeMeeting, hasJoined, joinMeeting, handleLeaveMeeting } = useVideoCall();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [addressBookOpen, setAddressBookOpen] = useState(false);
  const [socialMediaOpen, setSocialMediaOpen] = useState(false);

  const handleSectionClick = (sectionId) => {
    if (
      [
        'feed',
        'teams',
        'email',
        'notes',
        'filesync',
        'calendar',
        'minisite',
        'projects',
        'meeting',
      ].includes(sectionId)
    ) {
      setActiveView(sectionId);
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleMeetingJoin = (meetingData) => {
    joinMeeting(meetingData);
  };

  const handleMeetingClose = () => {
    handleLeaveMeeting();
  };

  useEffect(() => {
    const standardViews = [
      'dashboard',
      'noticeboard',
      'email',
      'feed',
      'notes',
      'filesync',
      'calendar',
      'minisite',
      'meeting',
    ];
    if (activeView && !standardViews.includes(activeView)) {
      setTimeout(() => {
        const element = document.getElementById(activeView);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [activeView]);

  const scrollToTop = () => {
    const contentDiv = document.querySelector('.dashboard-content');
    if (contentDiv) {
      contentDiv.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    setShowScrollTop(scrollTop > 200);
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return (
          <section id="dashboard" className="snap-section">
            <div className="dashboard-section-header">
              <h1 className="dashboard-section-title dashboard-title">
                {t('navigation.dashboard')}
              </h1>
              <IconMenu onSectionClick={handleSectionClick} />
            </div>
            <DashboardWidgets />
          </section>
        );
      case 'feed':
        return (
          <section id="feed" className="snap-section">
            <NewsFeedSection />
          </section>
        );
      case 'teams':
        return (
          <section id="teams" className="snap-section">
            <TeamsSection />
          </section>
        );
      case 'projects':
        return (
          <section id="projects" className="snap-section">
            <ProjectsSection />
          </section>
        );
      case 'notes':
        return (
          <section id="notes" className="snap-section">
            <NotesSection />
          </section>
        );
      case 'filesync':
        return (
          <section id="filesync" className="snap-section">
            <FileManagerSection />
          </section>
        );
      case 'minisite':
        return (
          <section id="minisite" className="snap-section">
            <MinisiteSection />
          </section>
        );
      case 'calendar':
        return (
          <section id="calendar" className="snap-section">
            <CalendarSection
              onMeetingJoin={handleMeetingJoin}
              activeMeeting={activeMeeting}
            />
          </section>
        );
      case 'meeting':
        return (
          <section id="meeting" className="snap-section">
            <MeetingsSection
              onMeetingJoin={handleMeetingJoin}
              activeMeeting={activeMeeting}
            />
          </section>
        );
      case 'email':
        return (
          <section id="email" className="snap-section">
            <EmailProvider>
              <EmailSection />
            </EmailProvider>
          </section>
        );
      case 'talk':
        return (
          <section
            id="talk"
            className="snap-section"
            style={{ height: '100%' }}
          >
            <TalkSection />
          </section>
        );
      case 'noticeboard':
        return (
          <section
            id="noticeboard"
            className="snap-section"
            style={{ height: 'calc(100vh - 80px)' }}
          >
            <NoticeboardSection />
          </section>
        );
      default:
        return (
          <div className="module-placeholder">
            <h1>{`${activeView.toUpperCase()} ${t('common.system.module_suffix')}`}</h1>
            <p>{t('common.system.module_in_development')}</p>
          </div>
        );
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar onAddressBookToggle={() => setAddressBookOpen((v) => !v)} onMediaToggle={() => setSocialMediaOpen((v) => !v)} />
      <div className="dashboard-main">
        <Header />
        <div className={`dashboard-content${activeView === 'email' ? ' dashboard-content--noscroll' : ''}`} onScroll={handleScroll}>
          {renderContent()}
          {showScrollTop && (
            <button className="scroll-to-top-btn" onClick={scrollToTop}>
              <FaChevronUp />
            </button>
          )}
        </div>
      </div>
      <NotificationSidebar />
      <AddressBookPanel
          isOpen={addressBookOpen}
          onClose={() => setAddressBookOpen(false)}
      />
      <MediaPanel
          isOpen={socialMediaOpen}
          onClose={() => setSocialMediaOpen(false)}
      />

      {activeMeeting && (
        <MeetingWindow
          key={`${activeMeeting.source || 'instant'}-${activeMeeting.id}-${activeMeeting.occurrence_date || ''}`}
          meeting={activeMeeting}
          tokens={activeMeeting.tokens}
          startJoined={hasJoined}
          onClose={handleMeetingClose}
        />
      )}
    </div>
  );
};

export default Dashboard;
